import { useCallback, useEffect, useState } from "react";
import { Download, Search, ChevronDown, ChevronRight, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELD = "border border-ink/15 py-2 px-3 text-sm bg-white focus:border-klein focus:outline-none";
const EMPTY = { q: "", actor: "", resource: "", date_from: "", date_to: "" };

// Admin-only trail of every change made in the app: who, when, what (before → after).
export default function ActivityLog() {
    const [filters, setFilters] = useState(EMPTY);
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, actors: [], resources: [], page_size: 50 });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/audit-log", { params: { ...filters, page, page_size: 50 } });
            setData(d);
        } catch {
            toast.error("Could not load the activity log");
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => { load(); }, [load]);

    const setF = (k) => (e) => { setPage(1); setFilters((f) => ({ ...f, [k]: e.target.value })); };

    const exportCsv = async () => {
        try {
            const { data: blob } = await api.get("/audit-log/export.csv", { params: filters, responseType: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "activity_log.csv";
            a.click();
        } catch {
            toast.error("Export failed");
        }
    };

    const pages = Math.max(1, Math.ceil(data.total / (data.page_size || 50)));

    return (
        <div className="p-4 sm:p-8 lg:p-12 max-w-7xl" data-testid="activity-log-page">
            <div className="overline">Admin</div>
            <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink flex items-center gap-3">
                    <History className="w-9 h-9 text-klein" /> Activity log
                </h1>
                <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors" data-testid="activity-export-btn">
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>
            <p className="mt-2 text-sm text-ink/60">Every create, edit, delete, approval and sign-in — who did it, when, and what changed.</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="activity-filters">
                <label className="relative lg:col-span-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input value={filters.q} onChange={setF("q")} placeholder="Search action, user, path…" className={`${FIELD} w-full pl-9`} data-testid="activity-search" />
                </label>
                <select value={filters.actor} onChange={setF("actor")} className={FIELD} data-testid="activity-actor">
                    <option value="">All users</option>
                    {data.actors.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={filters.resource} onChange={setF("resource")} className={FIELD} data-testid="activity-resource">
                    <option value="">All areas</option>
                    {data.resources.map((r) => <option key={r} value={r}>{r.replace(/-/g, " ")}</option>)}
                </select>
                <div className="flex gap-2">
                    <input type="date" value={filters.date_from} onChange={setF("date_from")} className={`${FIELD} w-full`} data-testid="activity-date-from" />
                    <input type="date" value={filters.date_to} onChange={setF("date_to")} className={`${FIELD} w-full`} data-testid="activity-date-to" />
                </div>
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="activity-table">
                    <thead>
                        <tr className="border-b border-ink/10 bg-bone/50">
                            <th className="w-8" />
                            <th className="text-left overline px-3 py-3">When</th>
                            <th className="text-left overline px-3 py-3">User</th>
                            <th className="text-left overline px-3 py-3">Action</th>
                            <th className="text-left overline px-3 py-3">Changes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/40">Loading…</td></tr>}
                        {!loading && data.items.length === 0 && (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/40" data-testid="activity-empty">No activity matches these filters</td></tr>
                        )}
                        {!loading && data.items.map((r) => <LogRow key={r.id} row={r} />)}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm text-ink/60">
                <span data-testid="activity-total">{data.total.toLocaleString()} entries</span>
                <div className="flex items-center gap-2">
                    <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border border-ink/15 px-3 py-1.5 disabled:opacity-40" data-testid="activity-prev">Prev</button>
                    <span>Page {page} / {pages}</span>
                    <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="border border-ink/15 px-3 py-1.5 disabled:opacity-40" data-testid="activity-next">Next</button>
                </div>
            </div>
        </div>
    );
}

function LogRow({ row }) {
    const [open, setOpen] = useState(false);
    const changes = Object.entries(row.changes || {});
    return (
        <>
            <tr className="border-b border-ink/5 hover:bg-bone/40 align-top" data-testid={`activity-row-${row.id}`}>
                <td className="pl-3 py-3">
                    {changes.length > 0 && (
                        <button onClick={() => setOpen((o) => !o)} className="text-ink/50 hover:text-ink" data-testid={`activity-expand-${row.id}`}>
                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                    )}
                </td>
                <td className="px-3 py-3 whitespace-nowrap tabular-nums text-ink/70">{new Date(row.at).toLocaleString()}</td>
                <td className="px-3 py-3">
                    <div className="text-ink">{row.actor_name || row.actor_email}</div>
                    <div className="text-[11px] text-ink/40">{row.actor_email} · {(row.actor_roles || []).join(", ")}</div>
                </td>
                <td className="px-3 py-3 text-ink">{row.summary}</td>
                <td className="px-3 py-3 text-ink/60 text-xs">
                    {changes.length ? `${changes.length} field${changes.length > 1 ? "s" : ""}: ${changes.map(([k]) => k.replace(/_/g, " ")).slice(0, 3).join(", ")}${changes.length > 3 ? "…" : ""}` : "—"}
                </td>
            </tr>
            {open && (
                <tr className="bg-bone/30 border-b border-ink/5" data-testid={`activity-changes-${row.id}`}>
                    <td />
                    <td colSpan={4} className="px-3 py-3">
                        <div className="grid gap-1.5 text-xs">
                            {changes.map(([k, [before, after]]) => (
                                <div key={k} className="grid grid-cols-[160px_1fr] gap-3">
                                    <span className="text-ink/50">{k.replace(/_/g, " ")}</span>
                                    <span className="break-words">
                                        <span className="line-through text-red-700/70">{before || "∅"}</span>
                                        <span className="mx-2 text-ink/40">→</span>
                                        <span className="text-emerald-800">{after || "∅"}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
